import {
  Button,
  ButtonProps,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { useGetListingReportPdfLazyQuery } from '@client/graphql/__generated__/main-operations';
import { DownloadIcon, Share2Icon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ModalTrigger } from '~/common/components/Modal/ModalTrigger';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { useCreateBuyerOfferAnalysis } from '../../../BuyerOfferAnalysis/hooks/useCreateBuyerOfferAnalysis';
import { CreateOfferScorecardButton } from '../CreateOfferScorecardButton';
import { useListingPageContext } from '../ListingPageContext';
import { ShareListingReportWithClientModal } from '../ShareListingReportWithClientModal';

export const ShareWithClientButton = ({
  iconOnly = true,
}: {
  iconOnly?: boolean;
}) => {
  const { isViewOnly } = useListingPageContext();

  if (isViewOnly) {
    return null;
  }

  const button = iconOnly ? (
    <Tooltip label="Share with Client">
      <IconButton
        aria-label="Share with Client"
        icon={<Icon as={Share2Icon} boxSize={5} />}
        px={2}
        size="sm"
        variant="outline"
      />
    </Tooltip>
  ) : (
    <Button
      leftIcon={<Icon as={Share2Icon} boxSize={5} />}
      size="sm"
      variant="outline"
    >
      Share
    </Button>
  );

  return (
    <ModalTrigger modal={ShareListingReportWithClientModal}>
      {button}
    </ModalTrigger>
  );
};

export const OfferScorecardButton = (
  props: Omit<ButtonProps, 'onClick' | 'isLoading'>
) => {
  const { listing, isOffMarketListing, isViewOnly } = useListingPageContext();
  const navigate = useNavigate();

  const { handleCreate, loading: isCreatingScorecard } =
    useCreateBuyerOfferAnalysis();

  if (isViewOnly || isOffMarketListing) {
    return null;
  }

  const onCreateOfferScorecard = async () => {
    if (!listing?.id) {
      return;
    }

    const boa = await handleCreate({ listingId: listing.id });
    if (!boa?.id) {
      return;
    }
    navigate(`/offer-scorecard/${boa.id}`);
  };

  return (
    <CreateOfferScorecardButton
      isLoading={isCreatingScorecard}
      onClick={() => void onCreateOfferScorecard()}
      {...props}
    />
  );
};

export const DownloadPdfButton = ({
  iconOnly = true,
}: {
  iconOnly?: boolean;
}) => {
  const { listing, propertyDetails, isViewOnly } = useListingPageContext();
  const [getListingReportPdf, { loading }] = useGetListingReportPdfLazyQuery();
  const toast = useToast();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const onDownload = async () => {
    const { data } = await getListingReportPdf({
      variables: {
        input: {
          listingId: listing?.id,
          propertyDetailsId: propertyDetails?.id,
        },
      },
    });

    const url = data?.getListingReportPdf?.file?.url;

    if (url) {
      setDownloadUrl(url);
      toast({
        title: 'PDF Generated',
        description: (
          <Flex alignItems="center" gap={2} justifyContent="space-between">
            <Text>Your PDF is ready.</Text>
            <Button as="a" href={url} size="sm" target="_blank">
              Download PDF
            </Button>
          </Flex>
        ),
        duration: null,
        isClosable: true,
        onCloseComplete() {
          setDownloadUrl(null);
        },
        position: isMobile ? 'bottom' : 'top-right',
      });
    }
  };

  if (isViewOnly) {
    return null;
  }

  const button = (
    <Tooltip
      shouldWrapChildren
      label={
        loading
          ? 'Generating PDF, this may take a few seconds...'
          : 'Generate PDF'
      }
    >
      {iconOnly ? (
        <IconButton
          aria-label="Generate PDF"
          icon={<Icon as={DownloadIcon} boxSize={5} />}
          isDisabled={!!downloadUrl}
          isLoading={loading}
          px={2}
          size="sm"
          variant="outline"
          onClick={() => void onDownload()}
        />
      ) : (
        <Button
          isDisabled={!!downloadUrl}
          isLoading={loading}
          leftIcon={<Icon as={DownloadIcon} boxSize={5} />}
          loadingText="Generating"
          size="sm"
          variant="outline"
          onClick={() => void onDownload()}
        >
          PDF
        </Button>
      )}
    </Tooltip>
  );

  return button;
};
